/* eslint-disable camelcase */
import { setAuthToken, removeAuthToken, getAuthToken } from '../utils/authToken';
import client from '../utils/client';
import { setAnalyticsUser } from '../utils/mixpanel';
import { authReason, beginAuthAttempt, finishAuthAttempt, hasAuthenticatedUser } from '../utils/authAnalytics';
import {
  ONBOARDING_SIGNUP_EVENTS,
  SIGNUP_METHOD_EMAIL,
  classifySignupError,
  getEmailTrackingProperties,
  trackOnboardingSignup,
} from '../utils/onboardingSignupAnalytics';

export const setUserSession = (data) => {
  setAnalyticsUser(data);
  return { type: 'USER_SET_SESSION', data };
};

export const setUserMyNewsView = (myNewsView) => ({
  type: 'USER_SET_MYNEWS_VIEW',
  myNewsView,
});

export const signOut = () => {
  setAnalyticsUser(null);
  removeAuthToken();

  try {
    window.sessionStorage.removeItem('spotlight_pending_username');
  } catch {
    // Storage can be unavailable in restricted browser modes.
  }

  return { type: 'USER_SIGN_OUT' };
};

export const fetchUser = () => {
  const url = '/users/me';
  return new Promise((resolve, reject) => {
    client().get(url)
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

export const checkUniqueNameAvailability = async (uniqueName) => {
  const url = '/users/unique_name/availability';
  const apiClient = getAuthToken() ? client() : client(false);
  const response = await apiClient.get(url, {
    params: { unique_name: uniqueName },
  });
  return response?.data || response;
};

const messages = {
  404: 'Incorrect email or password.',
  412: 'You have to confirm your email address before continuing.',
};

export const authenticate = async ({ email, password, rememberMe, dispatch, analyticsAttempt }) => {
  const attempt = analyticsAttempt || beginAuthAttempt('signin', 'email');
  const url = '/users/token';
  const data = { auth: { email, password } };
  try {
    const response = await client().post(url, data);
    if (typeof response?.data?.jwt !== 'string' || !response.data.jwt.trim()) {
      throw new Error('Missing authenticated session token');
    }
    setAuthToken(response.data.jwt, rememberMe);
    const userResponse = await fetchUser();
    if (!hasAuthenticatedUser(userResponse.data)) throw new Error('Invalid authenticated session');
    dispatch(setUserSession(userResponse.data));
    finishAuthAttempt(attempt, 'success');
    return { success: true, user: userResponse.data };
  } catch (e) {
    finishAuthAttempt(attempt, 'failure', authReason(e));
    if (e.status === 404 || e.status === 412) {
      return {
        success: false,
        code: e.status,
        error: e,
        message: messages[e.status],
      };
    }
    throw e;
  }
};

export const authenticateGuest = () => async (dispatch) => {
  const url = '/users/token/guest';
  try {
    const response = await client().post(url);
    setAuthToken(response.data.jwt);
    const userResponse = await fetchUser();
    dispatch(setUserSession(userResponse.data));
    return { success: true, user: userResponse.data };
  } catch (err) {
    if (err.status !== 404) {
      console.log('error');
    }
  }
  return null;
};

export const authenticateDelete = async ({ email, password }) => {
  const url = '/users/deletion_token ';
  const data = { auth: { email, password } };
  try {
    const response = await client().post(url, data);
    return response;
  } catch (e) {
    if (e.status === 404 || e.status === 412 || e.status === 422) {
      throw Error(messages[e.status]);
    }
  }
  return null;
};

export const loginSubmit = (data) => async (dispatch) => {
  const auth = await authenticate({ dispatch, ...data });

  if (!auth.success) {
    return Promise.reject(auth.message || auth.error);
  }
  return auth.user;
};

export const signUp = ({
  email, uniqueName, password, captchaToken, landingCode,
}) => async (dispatch) => {
  const analyticsAttempt = beginAuthAttempt('signup', SIGNUP_METHOD_EMAIL);
  const data = {
    email,
    unique_name: uniqueName,
    password,
    captcha_token: captchaToken,
    landing_code: landingCode,
  };

  try {
    if (!getAuthToken()) {
      const urlToken = '/users/token/guest';
      const response = await client().post(urlToken);
      const jwt = response?.data?.jwt;
      setAuthToken(jwt);
    }

    const userResponse = await fetchUser();
    const userId = userResponse?.data?.id;
    const url = `/users/${userId}/registration`;
    await client().put(url, data);

    const { is_edu_email: isEduEmail } = getEmailTrackingProperties(email);
    trackOnboardingSignup(ONBOARDING_SIGNUP_EVENTS.succeeded, {
      user_id: userId || null,
      is_edu_email: isEduEmail,
      signup_method: SIGNUP_METHOD_EMAIL,
      outcome_stage: 'registration',
    });
  } catch (e) {
    finishAuthAttempt(analyticsAttempt, 'failure', authReason(e));
    trackOnboardingSignup(ONBOARDING_SIGNUP_EVENTS.failed, {
      error_type: classifySignupError(e),
      signup_method: SIGNUP_METHOD_EMAIL,
    });

    if (e.status === 422) {
      return Promise.reject(e.data?.message || e);
    }
    throw e;
  }
  const auth = await authenticate({
    email, password, dispatch, rememberMe: true, analyticsAttempt,
  });
  return { auth };
};

export const updateUser = async (userId, data) => {
  const url = `/users/${userId}`;
  try {
    const response = await client().patch(url, data);
    return response.data;
  } catch (e) {
    return {
      status: e.status,
      message: e.data.message,
    };
  }
};

export const updateUserEmail = async (userId, newEmail) => {
  const url = `/users/${userId}/email`;

  return client().patch(url, { new_email: newEmail });
};

export const updateUnconfirmedEmail = async (userId, email) => {
  const url = `/users/${userId}/unconfirmed_email`;
  try {
    return client().patch(url, { new_email: email });
  } catch (e) {
    console.log(e);
  }
  return null;
};

export const updateSignupDetails = ({
  userId, currentEmail, currentUniqueName, email, uniqueName,
}) => async (dispatch) => {
  if (uniqueName !== currentUniqueName) {
    const profileResponse = await updateUser(userId, { unique_name: uniqueName });

    if (profileResponse?.status) {
      return Promise.reject(profileResponse.message || profileResponse);
    }
  }

  if (email !== currentEmail) {
    const url = `/users/${userId}/unconfirmed_email`;

    try {
      await client().patch(url, { new_email: email });
    } catch (e) {
      if (e.status === 422) {
        return Promise.reject(e.data?.message || e);
      }
      throw e;
    }
  }

  const userResponse = await fetchUser();
  dispatch(setUserSession(userResponse.data));
  return userResponse.data;
};

export const resendConfirmationEmail = (email) => {
  const url = '/users/reconfirmation';
  return client(false).post(url, { email });
};

export const forgotPassword = (email) => {
  const url = '/users/password/reset';
  const data = { email };
  return client().post(url, data);
};

export const forgotPasswordConfirm = async (token, password) => {
  const url = '/users/password/reset';
  const data = {
    token,
    password,
  };
  try {
    return await client().patch(url, data);
  } catch (e) {
    if (e.status === 404) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject('The password reset token is wrong or expired.');
    }
    throw e;
  }
};

const deleteAccountMessages = {
  403: 'User not authorized.',
  404: 'No user found with this email and password.',
  412: 'Can\'t delete the user. The user has active managed subscriptions.',
  422: 'Token expired.',
};

export const deleteAccount = async (token) => {
  const url = `/users/token/${token}`;

  try {
    return await client().delete(url);
  } catch (e) {
    if (e.status === 404 || e.status === 412 || e.status === 422) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject(
        deleteAccountMessages[e.status],
      );
    }
    throw e;
  }
};

export const deleteAccountConfirm = async (token) => {
  const url = `/users/token/${token}`;
  try {
    return await client().delete(url);
  } catch (e) {
    if (e.status === 404) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject('The confirmation token is wrong or expired.');
    }
    if (e.status === 422) {
      return Promise.reject(e.data?.message || e);
    }
    throw e;
  }
};

export const finishOnboarding = (userId) => async (dispatch) => {
  const url = `/users/${userId}/onboarding`;
  await client().put(url);
  dispatch({
    type: 'USER_FINISH_ONBOARDING',
  });
};

export const getSettings = async (userId) => {
  const url = `users/${userId}/settings/`;
  try {
    const data = await client().get(url);
    return data;
  } catch (err) {
    console.log(err.message);
  }
  return null;
};

export const updateSettings = async (userId, settings = null) => {
  if (settings) {
    const url = `users/${userId}/settings/`;
    try {
      const data = await client().patch(url, settings);
      return data;
    } catch (err) {
      console.log(err.message);
    }
  }
  return null;
};

export default null;
